import { LeadListScope } from '@up2stay/up2promo-api-client';
import { Button } from '@up2stay/up2promo-ui';
import { Card, CardContent } from '@up2stay/up2promo-ui';
import { Skeleton } from '@up2stay/up2promo-ui';
import {
  AlertCircle,
  Archive,
  Lock,
  Plus,
  ScanLine,
  UserRound,
  Users,
} from 'lucide-react';

import { LeadCardList } from '@/core/components/leads/lead-card-list';
import { LeadsBoardView } from '@/core/components/leads/leads-board-view';
import { LeadsScopeTabs } from '@/core/components/leads/leads-scope-tabs';
import type { UseLeadsPageResult } from '@/core/components/leads/use-leads-page';
import { ListEmptyState, ListPageBody } from '@/core/components/layout';

type LeadsBoardTabProps = {
  page: UseLeadsPageResult;
  showScopeRow?: boolean;
};

export function LeadsBoardTab({
  page,
  showScopeRow = true,
}: LeadsBoardTabProps) {
  const {
    dict,
    profile,
    currentWorkspace,
    leads,
    leadsError,
    isLoading,
    queryKey,
    handleLeadClick,
    handleLeadCreated,
    handleRefresh,
    resolveLeadCardMeta,
    leadCardLabelMaps,
    listScope,
    setListScope,
    scopeCounts,
    scopeLabels,
    canSeeOthersPrivate,
    requestCreateLead,
    createRequestId,
    createInitialStatus,
    setBusinessCardScanOpen,
    includeArchived,
    setIncludeArchived,
  } = page;

  const listDict = dict.leads.list as {
    emptyMineTitle: string;
    emptyMineDescription: string;
    emptyMineCta: string;
    emptyWorkspaceOnlyPrivateTitle: string;
    emptyWorkspaceOnlyPrivateDescription: string;
    emptyWorkspaceOnlyPrivateDescriptionOne: string;
    emptyWorkspaceOnlyPrivateCta: string;
    emptyOthersPrivateTitle: string;
    emptyOthersPrivateDescription: string;
    emptyWorkspaceTitle: string;
    emptyWorkspaceDescription: string;
    emptyWorkspaceScan: string;
    emptyWorkspaceAdd: string;
    scope?: {
      showArchived?: string;
      hideArchived?: string;
      boardArchivedHidden?: string;
    };
  };

  const showArchiveAffordance =
    includeArchived || scopeCounts.archivedHidden > 0;

  // DEV-536: once archived leads are on the board, say so.
  const archivedLabel = includeArchived
    ? (scopeLabels.archivedShown ?? '{count} archived shown').replace(
        '{count}',
        String(leads.filter((l) => l.archivedAt).length),
      )
    : (
        scopeLabels.archivedHidden ??
        listDict.scope?.boardArchivedHidden ??
        '{count} archived hidden'
      ).replace('{count}', String(scopeCounts.archivedHidden));
  const showArchivedLabel = listDict.scope?.showArchived ?? 'Anzeigen';
  const hideArchivedLabel = listDict.scope?.hideArchived ?? 'Ausblenden';
  const dropHereLabel = dict.leads.list.boardDropHere ?? 'Hier ablegen';

  const archiveFooter = showArchiveAffordance ? (
    <p className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
      <Archive className="size-3.5 shrink-0" aria-hidden />
      <span>
        {archivedLabel}
        {' · '}
        <button
          type="button"
          className="font-semibold text-[color:var(--ghost-accent-fg)]"
          onClick={() => setIncludeArchived(!includeArchived)}
        >
          {includeArchived ? hideArchivedLabel : showArchivedLabel}
        </button>
      </span>
    </p>
  ) : null;

  const isScopeEmpty = !isLoading && leads.length === 0;

  // Active board empty → L1/L3/L4; archive Anzeigen composes below when set.
  const emptyScopeState = isScopeEmpty ? (
    listScope === LeadListScope.MINE && scopeCounts.workspace > 0 ? (
      <ListEmptyState
        icon={<UserRound className="h-6 w-6 text-muted-foreground" />}
        title={listDict.emptyMineTitle}
        description={listDict.emptyMineDescription.replace(
          '{count}',
          String(scopeCounts.workspace),
        )}
        action={
          <Button
            type="button"
            variant="outline"
            onClick={() => setListScope(LeadListScope.WORKSPACE)}
          >
            {listDict.emptyMineCta}
          </Button>
        }
      />
    ) : listScope === LeadListScope.WORKSPACE && scopeCounts.mine > 0 ? (
      <ListEmptyState
        icon={<Lock className="h-6 w-6 text-muted-foreground" />}
        title={listDict.emptyWorkspaceOnlyPrivateTitle}
        description={
          scopeCounts.mine === 1
            ? listDict.emptyWorkspaceOnlyPrivateDescriptionOne
            : listDict.emptyWorkspaceOnlyPrivateDescription.replace(
                '{count}',
                String(scopeCounts.mine),
              )
        }
        action={
          <Button
            type="button"
            variant="outline"
            onClick={() => setListScope(LeadListScope.MINE)}
          >
            {listDict.emptyWorkspaceOnlyPrivateCta}
          </Button>
        }
      />
    ) : listScope === LeadListScope.OTHERS_PRIVATE ? (
      <ListEmptyState
        icon={<Lock className="h-6 w-6 text-muted-foreground" />}
        title={listDict.emptyOthersPrivateTitle}
        description={listDict.emptyOthersPrivateDescription}
      />
    ) : (
      <ListEmptyState
        icon={<Users className="h-6 w-6 text-muted-foreground" />}
        title={listDict.emptyWorkspaceTitle}
        description={listDict.emptyWorkspaceDescription}
        action={
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setBusinessCardScanOpen(true)}
            >
              <ScanLine className="mr-2 h-4 w-4" />
              {listDict.emptyWorkspaceScan}
            </Button>
            <Button type="button" onClick={() => requestCreateLead()}>
              <Plus className="mr-2 h-4 w-4" />
              {listDict.emptyWorkspaceAdd}
            </Button>
          </div>
        }
      />
    )
  ) : null;

  return (
    // Room below the inline create form (DEV-510 column "+ Lead") so the
    // fixed desktop board minimap never sits on top of its Save button.
    <ListPageBody className="md:pb-28">
      {leadsError ? (
        <ListEmptyState
          tone="error"
          icon={<AlertCircle className="h-6 w-6" aria-hidden />}
          title={dict.leads.loadError.title}
          description={dict.leads.loadError.description}
          action={
            <Button variant="outline" onClick={handleRefresh}>
              {dict.leads.loadError.retry}
            </Button>
          }
        />
      ) : null}
      {showScopeRow ? (
        <LeadsScopeTabs
          className="mb-3"
          value={listScope}
          onChange={setListScope}
          counts={scopeCounts}
          canSeeOthersPrivate={canSeeOthersPrivate}
          labels={{
            mine: scopeLabels.mine,
            workspace: scopeLabels.workspace,
            othersPrivate: scopeLabels.othersPrivate,
          }}
        />
      ) : null}
      {isLoading && leads.length === 0 ? (
        <div className="flex flex-col gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-5 sm:p-6">
                <Skeleton className="h-12 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : emptyScopeState ? (
        <div className="flex flex-col gap-3">
          {emptyScopeState}
          {archiveFooter}
        </div>
      ) : (
        <>
          <LeadsBoardView
            leads={leads}
            onLeadClick={handleLeadClick}
            queryKey={queryKey}
            leadCardMeta={resolveLeadCardMeta}
            labelMaps={leadCardLabelMaps}
            onAddLead={requestCreateLead}
            dropHereLabel={dropHereLabel}
          />
          {archiveFooter}
        </>
      )}
      <LeadCardList
        leads={[]}
        existingLeads={leads}
        profileId={profile?.id}
        workspaceId={currentWorkspace?.id ?? null}
        onLeadClick={handleLeadClick}
        onLeadCreated={handleLeadCreated}
        onScanBusinessCard={() => setBusinessCardScanOpen(true)}
        hideAddCards
        hideLeadCards
        createRequestId={createRequestId}
        createInitialStatus={createInitialStatus}
      />
    </ListPageBody>
  );
}
